import axios from 'axios';
import { Api, createUri } from './utils';

import type { SystemStatus } from '@consts/systems';
import type { BillingType, UISelection, WarrantyType } from '@consts/company';

const DATA_URL = createUri(Api.Systems, '/system/V1');

export enum SystemUrl {
    Counts = 'counts',
    Station = 'station',
    StationSearch = 'stationSearch',
    StationsByLocation = 'locationStations',
    Stations = 'stations',
    Config = 'config',
}

/**
 * Station search result for autocomplete dropdowns
 */
export type StationSearchResult = {
    StationID: number;
    SerialNum: string;
};

/**
 * LocationID, System Count
 */
export type SystemCounts = Record<string, number>;

export type System = {
    StationID: number | null;
    LocationID: number | null;
    CompanyID: number | null;
    ResellerID: number | null;
    Model: number | null;
    Hostname: string;
    CreatedDate: string;
    SerialNum: string | null;
    Status: SystemStatus | null;
    LastConnected: string | null;
    ConfigHash: string | null;
    ProductID: string | null;
    Notes: string | null;
    CutPlanID: BillingType | null;
    WarrantyPlanID: WarrantyType | null;
    UISelectionID: UISelection;
    ProductCodeEntryEnabled?: 0 | 1;
    AppVersion: string | null;
    planChangeRequest?: {
        PlanChangeRequestID: number;
        StationID: number;
        EffectiveDate: string;
        CutPlanID: BillingType;
        CreatedByUserEmail: string;
        Status: 'Approved' | 'Pending';
    };
    stationMoveRequest?: {
        StationMoveRequestID: number;
        StationID: number;
        LocationID: number;
        EffectiveDate: string; // ISO
        CreatedByUserEmail: string;
        Status: 'Pending';
    };
};

export type SystemData = {
    totalStationsInQuery: string;
    stationId: number | null;
    locationId: number | null;
    companyId: number | null;
    resellerCompanyId: number | null;
    model: number | null;
    hostname: string;
    createdDate: string;
    serialNum: null | string;
    status: SystemStatus;
};

export const systemApi = axios.create({
    baseURL: DATA_URL,
});

export type SystemPayload = {
    stationId: number;
    locationId: number | null;
    resellerCompanyId: number | null;
    companyId: number | null;
    status: string;
};

export const fetchSystemData = async <T>(token: string, url: SystemUrl, params?: Record<string, string | number>) => {
    const resp = await systemApi.get<T>(url, {
        params,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    return resp.data;
};

export const fetchSystemCSV = async (token: string, params?: Record<string, string | number>) => {
    const resp = await systemApi.get('systemsCSV', {
        params,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    return resp.data;
};

export const requestPlanChange = async (
    token: string,
    systemId: number,
    justification: string,
    effectiveDate: string,
    planId: BillingType
): Promise<{ success: boolean; message: string }> => {
    const resp = await systemApi.post(
        'requestPlanChange',
        { justification, stationId: systemId, cutPlanId: planId, effectiveDate },
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }
    );

    if (resp.status !== 200) {
        return { success: false, message: resp.data.message };
    }

    return { success: true, message: 'Plan change requested' };
};

export const deletePlanChange = async (
    token: string,
    planId: number
): Promise<{ success: boolean; message: string }> => {
    const resp = await systemApi.delete(`requestPlanChange?planId=${planId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });

    if (resp.status !== 200) {
        return { success: false, message: resp.data.message };
    }

    return { success: true, message: 'Plan change deleted' };
};

// Update the status of a pending billing-plan change.
// NOTE: API now expects the unique plan-change identifier (planId) rather than stationId.
export const updatePlanStatus = async (token: string, planId: number, status: 'Approved' | 'Rejected') => {
    const resp = await systemApi.put(
        'managePlanChange',
        {
            planId,
            status,
        },
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }
    );

    if (resp.status !== 200) {
        return { success: false, message: resp.data.message };
    }

    return { success: true, message: 'Plan status updated' };
};

export const getPendingPlanChanges = async (token: string) => {
    const resp = await systemApi.get('managePlanChange', {
        headers: { Authorization: `Bearer ${token}` },
    });

    return resp.data;
};

export const getRecentBillingChanges = async (token: string) => {
    const resp = await systemApi.get('recentPlanChangeActivity', {
        headers: { Authorization: `Bearer ${token}` },
    });

    return resp.data;
};

// Station Move APIs
export const requestStationMove = async (
    token: string,
    stationId: number,
    locationId: number,
    effectiveDate: string
): Promise<{ success: boolean; message: string; moveRequestId?: number }> => {
    const resp = await systemApi.post(
        'requestStationMove',
        { stationId, locationId, effectiveDate },
        {
            headers: { Authorization: `Bearer ${token}` },
        }
    );

    if (resp.status !== 200) {
        return { success: false, message: resp.data?.message ?? 'Failed to request station move' };
    }

    return {
        success: true,
        message: resp.data?.message ?? 'Station move requested',
        moveRequestId: resp.data?.moveRequestId,
    };
};

export const cancelStationMove = async (
    token: string,
    moveRequestId: number
): Promise<{ success: boolean; message: string }> => {
    // DELETE with body payload
    const resp = await systemApi.delete('requestStationMove', {
        headers: { Authorization: `Bearer ${token}` },
        data: { moveRequestId },
    });

    if (resp.status !== 200) {
        return { success: false, message: resp.data?.message ?? 'Failed to cancel station move' };
    }

    return { success: true, message: resp.data?.message ?? 'Station move request cancelled successfully' };
};

export const getStationMoveRequests = async (token: string) => {
    const resp = await systemApi.get('stationMoveRequests', {
        headers: { Authorization: `Bearer ${token}` },
    });
    return resp.data;
};

export const getRecentStationMoveActivity = async (token: string) => {
    const resp = await systemApi.get('recentStationMoveActivity', {
        headers: { Authorization: `Bearer ${token}` },
    });
    return resp.data;
};
